<script setup>
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import { useMessageContext } from '../provider.js';

const { content, contentAttributes } = useMessageContext();

const referral = computed(() => contentAttributes.value?.referral || {});

const hasReferral = computed(() => Object.keys(referral.value).length > 0);

const mediaType = computed(() =>
  String(
    referral.value.media_type || referral.value.mediaType || ''
  ).toLowerCase()
);

const mediaUrl = computed(() => {
  if (mediaType.value === 'video') {
    return (
      referral.value.thumbnail_url ||
      referral.value.thumbnailUrl ||
      referral.value.video_url ||
      referral.value.videoUrl
    );
  }
  if (mediaType.value === 'image') {
    return referral.value.image_url || referral.value.imageUrl;
  }
  return (
    referral.value.thumbnail_url ||
    referral.value.thumbnailUrl ||
    referral.value.image_url ||
    referral.value.imageUrl ||
    referral.value.video_url ||
    referral.value.videoUrl ||
    null
  );
});

const headline = computed(() => referral.value.headline);
const body = computed(() => referral.value.body);
const sourceUrl = computed(
  () => referral.value.source_url || referral.value.sourceUrl
);

const hasImageError = ref(false);
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="whatsapp-ad-referral">
    <!-- Ad Referral Card -->
    <div
      v-if="hasReferral"
      class="mb-3 rounded-lg border border-n-slate-4 bg-n-slate-1 overflow-hidden"
    >
      <div
        class="flex items-center gap-1.5 px-3 py-2 bg-n-slate-2 border-b border-n-slate-4 text-xs font-medium text-n-slate-11"
      >
        <span class="i-lucide-megaphone size-3.5" />
        <span>{{ $t('CONVERSATION.AD_REFERRAL') || 'Ad Referral' }}</span>
      </div>

      <div class="p-3">
        <a
          v-if="sourceUrl"
          :href="sourceUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="block hover:opacity-90 transition-opacity"
        >
          <!-- Media Preview -->
          <div
            v-if="mediaUrl"
            class="mb-3 w-full h-32 bg-n-slate-3 rounded-md overflow-hidden relative"
          >
            <img
              v-if="!hasImageError"
              :src="mediaUrl"
              alt="Ad Media"
              class="w-full h-full object-cover"
              @error="hasImageError = true"
            />
            <div
              v-else
              class="flex flex-col items-center justify-center w-full h-full bg-n-slate-2 text-n-slate-11 p-2 text-center border-b border-n-slate-3"
            >
              <span class="i-lucide-image-off size-5 mb-1 opacity-75" />
              <span class="text-[10px] leading-tight">{{
                $t('CONVERSATION.IMAGE_EXPIRED') || 'Image expired'
              }}</span>
            </div>
            <div
              v-if="mediaType === 'video'"
              class="absolute inset-0 flex items-center justify-center pointer-events-none"
            >
              <div
                class="flex items-center justify-center size-10 rounded-full bg-black/50 backdrop-blur-sm ring-1 ring-white/20 shadow-md"
              >
                <span class="i-lucide-play size-5 text-white ml-0.5" />
              </div>
            </div>
          </div>

          <!-- Headline -->
          <h4
            v-if="headline"
            class="text-sm font-semibold text-n-slate-12 mb-1"
          >
            {{ headline }}
          </h4>

          <!-- Body Text -->
          <p v-if="body" class="text-xs text-n-slate-11 line-clamp-3">
            {{ body }}
          </p>
        </a>

        <!-- Fallback if no URL -->
        <div v-else>
          <div
            v-if="mediaUrl"
            class="mb-3 w-full h-32 bg-n-slate-3 rounded-md overflow-hidden relative"
          >
            <img
              v-if="!hasImageError"
              :src="mediaUrl"
              alt="Ad Media"
              class="w-full h-full object-cover"
              @error="hasImageError = true"
            />
            <div
              v-else
              class="flex flex-col items-center justify-center w-full h-full bg-n-slate-2 text-n-slate-11 p-2 text-center border-b border-n-slate-3"
            >
              <span class="i-lucide-image-off size-5 mb-1 opacity-75" />
              <span class="text-[10px] leading-tight">{{
                $t('CONVERSATION.IMAGE_EXPIRED') || 'Image expired'
              }}</span>
            </div>
            <div
              v-if="mediaType === 'video'"
              class="absolute inset-0 flex items-center justify-center pointer-events-none"
            >
              <div
                class="flex items-center justify-center size-10 rounded-full bg-black/50 backdrop-blur-sm ring-1 ring-white/20 shadow-md"
              >
                <span class="i-lucide-play size-5 text-white ml-0.5" />
              </div>
            </div>
          </div>
          <h4
            v-if="headline"
            class="text-sm font-semibold text-n-slate-12 mb-1"
          >
            {{ headline }}
          </h4>
          <p v-if="body" class="text-xs text-n-slate-11">
            {{ body }}
          </p>
        </div>
      </div>
    </div>

    <!-- Message body (the greeting text sent by the user) -->
    <p v-if="content" class="text-sm text-n-slate-12 mt-1">
      {{ content }}
    </p>
  </BaseBubble>
</template>
